import React, { ReactElement, useMemo } from 'react'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { IBroadcastFormRecipientResponseReportRow } from 'types/BroadcastForm'

interface ResponseTimelineChartProps {
  filteredRecipients: IBroadcastFormRecipientResponseReportRow[]
}

interface TimelineDataPoint {
  date: string
  responses: number
  dateLabel: string
}

// Custom tooltip component
const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload || !payload.length) {
    return null
  }

  return (
    <div
      className="bg-white border rounded shadow-lg p-3"
      style={{
        backgroundColor: 'white',
        border: '1px solid #ccc',
        borderRadius: '4px',
        padding: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      }}
    >
      <p className="font-weight-bold mb-2" style={{ fontWeight: 'bold', marginBottom: '8px' }}>
        {label}
      </p>
      {payload.map((entry: any) => (
        <p key={entry.dataKey} style={{ margin: '4px 0', color: entry.color }}>
          <span style={{ fontWeight: 'bold' }}>
            {entry.name}
            :
          </span>
          {' '}
          {entry.value}
        </p>
      ))}
    </div>
  )
}

export const ResponseTimelineChart: React.FC<ResponseTimelineChartProps> = ({
  filteredRecipients,
}): ReactElement => {
  const chartData = useMemo(() => {
    // Filter to only responses that have a response date
    const responsesWithDate = filteredRecipients.filter(
      (r) => r.HasResponse && r.ResponseAtUtc != null,
    )

    if (responsesWithDate.length === 0) {
      return []
    }

    // Deduplicate by recipient (ProfileId + FormSessionId) since we want to count
    // unique surveys responded to, not individual question responses
    const uniqueResponsesMap = new Map<string, IBroadcastFormRecipientResponseReportRow>()

    responsesWithDate.forEach((response) => {
      const key = `${response.ProfileId}-${response.FormSessionId}`
      if (!uniqueResponsesMap.has(key)) {
        uniqueResponsesMap.set(key, response)
      }
    })

    const uniqueResponses = Array.from(uniqueResponsesMap.values())

    // Group unique survey responses by date (day)
    const dateMap = new Map<string, number>()

    uniqueResponses.forEach((response) => {
      if (!response.ResponseAtUtc) return

      const responseDate = new Date(response.ResponseAtUtc)
      // Use YYYY-MM-DD as the key for grouping by day
      const dateKey = responseDate.toISOString().split('T')[0]

      dateMap.set(dateKey, (dateMap.get(dateKey) || 0) + 1)
    })

    // Convert to array and sort by date
    const data: TimelineDataPoint[] = Array.from(dateMap.entries())
      .map(([dateKey, count]) => {
        const date = new Date(dateKey)
        return {
          date: dateKey,
          responses: count,
          dateLabel: date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          }),
        }
      })
      .sort((a, b) => a.date.localeCompare(b.date))

    return data
  }, [filteredRecipients])

  if (chartData.length === 0) {
    return (
      <div className="text-center text-muted py-4">
        No response data available to display.
      </div>
    )
  }

  return (
    <ResponsiveContainer width="100%" height={300}>
      <LineChart
        data={chartData}
        margin={{
          top: 5, right: 30, left: 20, bottom: 5,
        }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          dataKey="dateLabel"
          angle={-45}
          textAnchor="end"
          height={80}
          style={{ fontSize: '12px' }}
        />
        <YAxis
          label={{ value: 'Number of Responses', angle: -90, position: 'insideLeft' }}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend />
        <Line
          type="monotone"
          dataKey="responses"
          stroke="#3b82f6"
          strokeWidth={2}
          dot={{ r: 4 }}
          activeDot={{ r: 6 }}
          name="Responses"
        />
      </LineChart>
    </ResponsiveContainer>
  )
}
